import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useCameraPlans,
  useCameras,
  useDeleteCameraPlan,
} from "@/hooks/useCameraPlans";
import { CameraPlanCard } from "./CameraPlanCard";
import { CreateCameraPlanDialog } from "./CreateCameraPlanDialog";
import { CameraPlanDetailView } from "./CameraPlanDetailView";
import type { CameraPlan } from "@/types/camera-plans";

interface CameraPlansSectionProps {
  projectId: string;
  selectedPlanId?: string;
  basePath: string;
}

export function CameraPlansSection({
  projectId,
  selectedPlanId,
  basePath,
}: CameraPlansSectionProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { data: plans = [], isLoading } = useCameraPlans(projectId);
  const deletePlan = useDeleteCameraPlan();

  const selectedPlan = selectedPlanId
    ? plans.find((p) => p.id === selectedPlanId)
    : undefined;

  useEffect(() => {
    const isCameraPlanOverview =
      location.pathname.replace(/\/$/, "") === basePath;
    if (
      isCameraPlanOverview &&
      !isLoading &&
      !selectedPlanId &&
      plans.length === 1
    ) {
      navigate(`${basePath}/${plans[0].id}`, { replace: true });
    }
  }, [basePath, isLoading, location.pathname, navigate, plans, selectedPlanId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <CameraPlanDetailView
        plan={selectedPlan}
        projectId={projectId}
        onBack={() => navigate(basePath)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Camera Plans</h3>
          <p className="text-sm text-muted-foreground">
            {plans.length} plan{plans.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button offlineEdit onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium mb-2">No camera plans yet</h4>
            <p className="text-muted-foreground mb-4">
              Create a camera plan to manage cameras and attach a floor plan
              image or embed.
            </p>
            <Button offlineEdit onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {plans.map((plan) => (
            <CameraPlanCardWithCount
              key={plan.id}
              plan={plan}
              projectId={projectId}
              to={`${basePath}/${plan.id}`}
              onDelete={() => deletePlan.mutate({ id: plan.id, projectId })}
            />
          ))}
        </div>
      )}

      <CreateCameraPlanDialog
        projectId={projectId}
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={(id) => navigate(`${basePath}/${id}`)}
      />
    </div>
  );
}

function CameraPlanCardWithCount({
  plan,
  projectId,
  to,
  onDelete,
}: {
  plan: CameraPlan;
  projectId: string;
  to: string;
  onDelete: () => void;
}) {
  const { data: cameras = [] } = useCameras(plan.id);
  return (
    <CameraPlanCard
      plan={plan}
      cameraCount={cameras.length}
      to={to}
      onDelete={onDelete}
    />
  );
}
